import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCurrentUser from '@hooks/useCurrentUser'
import useTeam, { generateUrls, useTeamForm } from '@hooks/useTeam'

import EntitySelector from '@components/EntitySelector'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import { useSetState } from '@domain-group/react-hooks'

import type { ActionResult } from '@redux/modules/types'
import type { TeamModel } from '@models/types'

const MODAL_KEY = 'CreateTeamModal'

type ModalCallbacks = {
  closeModal: () => void,
  createTeam: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateTeamModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { createTeam } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const team = digObject(modalPayload, 'team', {})

  const [state, setState] = useSetState({ userId: team.user_id })
  const { userId } = state

  const newRecord = !team.id

  const {
    callbacks: {
      createTeam: createFn,
    },
    creating,
    updating,
  } = useTeam(team)

  const {
    entityState,
    entityState: {
      description,
      owner_id,
      title,
    },
    errors,
    handlers,
    saveEnabled: formSaveEnabled,
  } = useTeamForm(team, { validateOn: 'change' })

  const { isAgencyAdmin } = useCurrentUser()

  const createTeamPayload = {
    callbacks: {
      action: createFn,
      afterAction: ({ response }: { response: ActionResult<TeamModel> }) => {
        if (response.success){
          const prefixRoute = isAgencyAdmin ? `agencies/${owner_id}/manage` : undefined
          const { editTeamUrl } = generateUrls(response.data, { prefixRoute })

          window.location.href = editTeamUrl
        }
      },
    },
    entityParams: { ...entityState, user_id: userId },
    requestOptions: { include_team_user_ids: true },
    toastText: 'Team Created',
  }

  const saveEnabled = isAgencyAdmin ? formSaveEnabled && userId : formSaveEnabled

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Create' : 'Edit'} titleSecondLine="Team" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="Description" errorMessage={errors.description} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea
              name="description"
              value={description}
              style={{ height: 120, resize: 'vertical' }}
              {...handlers}
            />
          </Form.Field>

          {isAgencyAdmin && (
            <EntitySelector
              callbacks={{
                selectItem: user => setState({ userId: user ? user.id : null }),
              }}
              entityKey="users"
              entityTitleKey="full_name"
              label="* Lead Agent"
              marginTop="large"
              searchRequestOptions={{
                actable_types: ['Agent'],
                agency_id: owner_id,
              }}
              selectedItemId={userId}
            />
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          // @ts-expect-error TS2345
          onClick={() => createTeam(createTeamPayload)}
          size="large"
        >
          {saveEnabled ? 'Create Team' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateTeamModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
